package com.hyperether.goodjob.scenes.components

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.MaterialTheme
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextOverflow
import com.hyperether.goodjob.boxCornerShape
import com.hyperether.goodjob.models.Team
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.ic_users
import com.hyperether.goodjob.resources.no_matching_teams
import com.hyperether.goodjob.resources.no_teams_available
import com.hyperether.goodjob.resources.select_team
import com.hyperether.goodjob.resources.team
import com.hyperether.goodjob.resources.teams
import com.hyperether.goodjob.theme.BlueApp
import com.hyperether.goodjob.theme.EmployeeDetailsText
import com.hyperether.goodjob.theme.InputBorderColor
import com.hyperether.goodjob.theme.TextColor
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.goodjob.util.toScaleSp
import com.hyperether.resources.stringResource
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource


@Composable
fun TeamSelectorWeb(
    teamsFromDb: List<Team>,
    selectedTeams: MutableState<List<Team>>,
    onTeamsSelected: (List<Team>) -> Unit,
    onAddNewTeam: (String) -> Unit,
    onScrollStart: () -> Unit = {},
    onScrollEnd: () -> Unit = {},
    isEmployee: Boolean = false
) {
    val focusManager = LocalFocusManager.current
    var searchText by remember { mutableStateOf("") }
    var isDropdownVisible by remember { mutableStateOf(false) }

    val listState = rememberLazyListState()
    val selectedListState = rememberLazyListState()
    val interactionOngoing = remember { mutableStateOf(false) }
    var previousSelectedListSize by remember { mutableStateOf(0) }

    val coroutineScope = rememberCoroutineScope()

    LaunchedEffect(listState.isScrollInProgress) {
        if (!listState.isScrollInProgress && interactionOngoing.value) {
            onScrollEnd()
            interactionOngoing.value = false
        }
    }

    LaunchedEffect(selectedTeams.value.size) {
        if (selectedTeams.value.size > previousSelectedListSize) {
            selectedListState.animateScrollToItem(selectedTeams.value.size - 1)
        }
        previousSelectedListSize = selectedTeams.value.size
    }

    Column(
        modifier = Modifier.fillMaxWidth()
    ) {
        if (selectedTeams.value.isNotEmpty()) {
            Row(
                modifier = Modifier
                    .fillMaxWidth(),
                verticalAlignment = Alignment.Top
            ) {
                Spacer(modifier = Modifier.weight(1f))
                LazyRow(
                    state = selectedListState,
                    horizontalArrangement = Arrangement.spacedBy(8.toScaleDp()),
                    modifier = Modifier.weight(2f)
                ) {
                    items(selectedTeams.value) { team ->
                        SelectedChip(
                            item = team.name ?: "",
                            onRemove = {
                                val updated = selectedTeams.value - team
                                selectedTeams.value = updated
                                onTeamsSelected(updated)
                            },
                            allowRemove = !isEmployee
                        )
                    }
                }
            }
            Spacer(modifier = Modifier.height(8.toScaleDp()))
        }

        Row(
            modifier = Modifier
                .fillMaxWidth(),
            verticalAlignment = Alignment.Top
        ) {
            Row(
                modifier = Modifier
                    .weight(1f)
                    .height(56.toScaleDp()),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Image(
                    painter = painterResource(Res.drawable.ic_users),
                    modifier = Modifier.size(20.toScaleDp()),
                    contentDescription = null
                )
                Text(
                    text = stringResource(Res.string.teams),
                    color = EmployeeDetailsText,
                    fontSize = 14.toScaleSp(),
                    modifier = Modifier.padding(start = 20.toScaleDp()),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }

            Column(
                modifier = Modifier.weight(2f)
            ) {
                // Search Field
                OutlinedTextField(
                    value = searchText,
                    onValueChange = { searchText = it },
                    placeholder = {
                        Text(
                            text = stringResource(Res.string.select_team),
                            fontSize = 14.toScaleSp(),
                            color = Color.LightGray,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    },
                    singleLine = true,
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(56.toScaleDp())
                        .onFocusChanged { focusState ->
                            if (focusState.isFocused) {
                                isDropdownVisible = true
                            } else {
                                coroutineScope.launch {
                                    // On Web: Without delay, clicking on Dropdown item doesn't register onClick
                                    // because focus lost instantly hides the Dropdown, delay is needed to allow the onClick event to trigger
                                    delay(100)
                                    isDropdownVisible = false
                                }
                            }
                        },
                    trailingIcon = {
                        val trimmed = searchText.trim()
                        if (trimmed.isNotEmpty()) {
                            IconButton(onClick = {
                                if (teamsFromDb.find { it.name == trimmed } == null) {
                                    onAddNewTeam(trimmed)
                                    searchText = ""
                                    onScrollEnd()
                                    focusManager.clearFocus()
                                }
                            }) {
                                Icon(Icons.Default.Add, contentDescription = "Add Team")
                            }
                        }
                    },
                    keyboardOptions = KeyboardOptions.Default.copy(imeAction = ImeAction.Done),
                    keyboardActions = KeyboardActions(
                        onDone = {
                            val trimmed = searchText.trim()
                            if (trimmed.isNotEmpty()) {
                                if (teamsFromDb.find { it.name == trimmed } == null) {
                                    onAddNewTeam(trimmed)
                                    searchText = ""
                                    isDropdownVisible = false
                                    onScrollEnd()
                                }
                            }
                            focusManager.clearFocus()
                        }
                    ),
                    colors = OutlinedTextFieldDefaults.colors(
                        disabledBorderColor = InputBorderColor,
                        unfocusedBorderColor = InputBorderColor,
                        disabledTextColor = TextColor,
                        focusedBorderColor = BlueApp,
                        cursorColor = BlueApp
                    ),
                    enabled = !isEmployee
                )

                // Filtered teams
                val filteredTeams = teamsFromDb.filter {
                    it.name?.contains(searchText, ignoreCase = true) == true &&
                            selectedTeams.value.none { selected -> selected.name == it.name }
                }

                if (isDropdownVisible) {
                    Spacer(modifier = Modifier.height(8.toScaleDp()))

                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .border(
                                1.toScaleDp(),
                                InputBorderColor,
                                boxCornerShape()
                            )
                            .background(MaterialTheme.colors.surface)
                            .heightIn(min = 0.toScaleDp(), max = 200.toScaleDp())
                            .pointerInput(Unit) {
                                awaitPointerEventScope {
                                    while (true) {
                                        val event = awaitPointerEvent()
                                        if (event.changes.any { it.pressed }) {
                                            if (!interactionOngoing.value) {
                                                interactionOngoing.value = true
                                                onScrollStart()
                                            }
                                        }

                                        if (event.changes.all { !it.pressed } && !listState.isScrollInProgress) {
                                            if (interactionOngoing.value) {
                                                interactionOngoing.value = false
                                                onScrollEnd()
                                            }
                                        }
                                    }
                                }
                            }
                    ) {
                        LazyColumn(
                            state = listState,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            items(filteredTeams) { team ->
                                Row(
                                    modifier = Modifier.fillMaxWidth()
                                ) {
                                    DropdownMenuItem(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .clip(RoundedCornerShape(4.toScaleDp()))
                                            .pointerHoverIcon(PointerIcon.Hand),
                                        text = {
                                            Text(
                                                text = team.name ?: "",
                                                fontSize = 14.toScaleSp(),
                                                maxLines = 1,
                                                overflow = TextOverflow.Ellipsis
                                            )
                                        },
                                        onClick = {
                                            searchText = ""
                                            val updated = selectedTeams.value + team
                                            selectedTeams.value = updated
                                            onTeamsSelected(updated)
                                            focusManager.clearFocus()
                                            onScrollEnd()
                                        }
                                    )

                                }
                            }

                            val trimmed = searchText.trim()
                            val message = when {
                                filteredTeams.isEmpty() && trimmed.isNotEmpty() ->
                                    Res.string.no_matching_teams

                                filteredTeams.isEmpty() && trimmed.isEmpty() ->
                                    Res.string.no_teams_available

                                else -> null
                            }

                            message?.let {
                                item {
                                    Text(
                                        text = stringResource(message),
                                        fontSize = 14.toScaleSp(),
                                        color = TextColor,
                                        modifier = Modifier.padding(16.toScaleDp())
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}